import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../utils/translations';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Available languages with their display names and flags
export const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'zh', name: '中文', flag: '🇨🇳' }
];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [isDetecting, setIsDetecting] = useState(true);

  // Function to detect user's preferred language
  const detectLanguage = async () => {
    try {
      // First try saved preference
      const savedLanguage = localStorage.getItem('preferred-language');
      if (savedLanguage && AVAILABLE_LANGUAGES.find(lang => lang.code === savedLanguage)) {
        console.log('Using saved language preference:', savedLanguage);
        setLanguage(savedLanguage);
        setIsDetecting(false);
        return;
      }

      // Then try browser language detection
      const browserLang = navigator.language || navigator.userLanguage;
      console.log('Browser language:', browserLang);
      
      // Check for exact matches or language prefixes
      const langCode = browserLang.toLowerCase().split('-')[0];
      const supportedLang = AVAILABLE_LANGUAGES.find(lang => 
        browserLang.toLowerCase().startsWith(lang.code) || langCode === lang.code
      );
      
      if (supportedLang) {
        console.log('Language detected from browser:', supportedLang.code);
        setLanguage(supportedLang.code);
        setIsDetecting(false);
        return;
      }

      // Try IP-based geolocation as fallback for NL detection
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        
        console.log('Detected country:', data.country_code);
        
        if (data.country_code === 'NL') {
          console.log('Netherlands detected, switching to Dutch');
          setLanguage('nl');
        } else {
          console.log('Using default English');
          setLanguage('en');
        }
      } catch (geoError) {
        console.log('Geolocation failed, using default English');
        setLanguage('en');
      }
    } catch (error) {
      console.error('Error detecting language:', error);
      setLanguage('en');
    } finally {
      setIsDetecting(false);
    }
  };

  useEffect(() => {
    detectLanguage();
  }, []);

  // Function to manually change language
  const changeLanguage = (newLanguage) => {
    const supportedLang = AVAILABLE_LANGUAGES.find(lang => lang.code === newLanguage);
    if (supportedLang) {
      setLanguage(newLanguage);
      localStorage.setItem('preferred-language', newLanguage);
      console.log('Language manually changed to:', newLanguage);
    }
  };

  // Function to get translated text
  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        break;
      }
    }
    
    // Fallback to English if translation not found
    if (value === undefined) {
      let fallbackValue = translations.en;
      for (const k of keys) {
        if (fallbackValue && typeof fallbackValue === 'object') {
          fallbackValue = fallbackValue[k];
        } else {
          break;
        }
      }
      return fallbackValue || key;
    }
    
    return value || key;
  };

  const value = {
    language,
    setLanguage: changeLanguage,
    t,
    isDetecting,
    availableLanguages: AVAILABLE_LANGUAGES,
    getCurrentLanguage: () => AVAILABLE_LANGUAGES.find(lang => lang.code === language),
    isNL: language === 'nl',
    isEN: language === 'en',
    isES: language === 'es',
    isFR: language === 'fr',
    isDE: language === 'de',
    isZH: language === 'zh'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext; 